import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { PaginationInterfaceLF } from "app/types";
import {
  Group,
  GroupPayload,
  GroupThemeColors,
  LocalizedGroup,
  LocalizedGroupPayload,
  NutritionCategory,
} from "app/types/package";
import { AppDispatch } from "../store";
import { RootState } from "../reducers/hooks";
import { createQuery, getActiveConfig } from "app/utils/constants/common";
import {
  deleteGroupsThemeApi,
  deleteLocalizedGroupApi,
  getGroupListsApi,
  getGroupsThemeApi,
  getLocalizedGroupsApi,
  getNutritionCategoriesApi,
  postGroupApi,
  postGroupsThemeApi,
  postLocalizedGroupApi,
  postNutritionCategoryApi,
  putGroupApi,
  putGroupsThemeApi,
  putNutritionCategoryApi,
} from "app/services/groups";
import { errorHandler } from "app/shared/Service/errorHandler";
import { alertMessage } from "../actions/common";

type GroupsFilters = {
  storeId: number;
  store: string;
};
interface InitialState {
  groupsList: Group[];
  groupsPagination: PaginationInterfaceLF | null;
  groupsListRowPerPage: number;
  filters: GroupsFilters;
  isGroupsListLoading: boolean;
  isSubmitting: boolean;

  localizedGroups: LocalizedGroup[];
  localizedGroupsPagination: PaginationInterfaceLF | null;
  localizedGroupsListRowPerPage: number;
  isLocalizedGroupsListLoading: boolean;
  isLocalizedGroupsSubmitting: boolean;

  groupsThemes: GroupThemeColors[];
  groupThemesSubmitting: boolean;
  groupThemesLoading: boolean;

  nutritionCategories: NutritionCategory[];
  isNutritionCategoriesListLoading: boolean;
  isNutritionCategoriesSubmitting: boolean;
  nutritionCategoriesListRowPerPage: number;

  groupsOptionsList: Group[];
  groupsOptionsStoreId: number | null;
  isGroupsOptionsLoading: boolean;
}

const initialState: InitialState = {
  groupsList: [],
  groupsPagination: null,
  groupsListRowPerPage: 20,
  filters: null,
  isGroupsListLoading: false,
  isSubmitting: false,

  localizedGroups: [],
  localizedGroupsPagination: null,
  localizedGroupsListRowPerPage: 20,
  isLocalizedGroupsListLoading: false,
  isLocalizedGroupsSubmitting: false,

  groupsThemes: [],
  groupThemesSubmitting: false,
  groupThemesLoading: false,

  nutritionCategories: [],
  isNutritionCategoriesListLoading: false,
  isNutritionCategoriesSubmitting: false,
  nutritionCategoriesListRowPerPage: 20,

  groupsOptionsList: [],
  groupsOptionsStoreId: null,
  isGroupsOptionsLoading: false,
};

export const groupsSlice = createSlice({
  name: "groups",
  initialState,
  reducers: {
    setGroupsList: (
      state,
      action: PayloadAction<{
        groups: Group[];
        pagination: PaginationInterfaceLF;
      }>,
    ) => {
      state.groupsList = action.payload.groups;
      state.groupsPagination = action.payload.pagination;
    },
    setGroupsLoading: (state, action: PayloadAction<boolean>) => {
      state.isGroupsListLoading = action.payload;
    },
    setGroupsListRowPerPage: (state, action: PayloadAction<number>) => {
      state.groupsListRowPerPage = action.payload;
    },
    setGroupsFilters: (state, action: PayloadAction<GroupsFilters | null>) => {
      state.filters = action.payload;
    },
    setGroupSubmitting: (state, action: PayloadAction<boolean>) => {
      state.isSubmitting = action.payload;
    },

    setLocalizedGroupsList: (
      state,
      action: PayloadAction<{
        groups: LocalizedGroup[];
        pagination: PaginationInterfaceLF;
      }>,
    ) => {
      state.localizedGroups = action.payload.groups;
      state.localizedGroupsPagination = action.payload.pagination;
    },
    setLocalizedGroupsLoading: (state, action: PayloadAction<boolean>) => {
      state.isLocalizedGroupsListLoading = action.payload;
    },
    setLocalizedGroupsListRowPerPage: (
      state,
      action: PayloadAction<number>,
    ) => {
      state.localizedGroupsListRowPerPage = action.payload;
    },
    setLocalizedGroupsSubmitting: (state, action: PayloadAction<boolean>) => {
      state.isLocalizedGroupsSubmitting = action.payload;
    },
    setGroupsThemes: (state, action: PayloadAction<GroupThemeColors[]>) => {
      state.groupsThemes = action.payload;
    },
    setGroupThemesSubmitting: (state, action: PayloadAction<boolean>) => {
      state.groupThemesSubmitting = action.payload;
    },
    setGroupThemesLoading: (state, action: PayloadAction<boolean>) => {
      state.groupThemesLoading = action.payload;
    },

    setNutritionCategoriesList: (
      state,
      action: PayloadAction<NutritionCategory[]>,
    ) => {
      state.nutritionCategories = action.payload;
    },
    setNutritionCategoriesLoading: (state, action: PayloadAction<boolean>) => {
      state.isNutritionCategoriesListLoading = action.payload;
    },
    setNutritionCategoriesSubmitting: (
      state,
      action: PayloadAction<boolean>,
    ) => {
      state.isNutritionCategoriesSubmitting = action.payload;
    },
    setNutritionCategoriesListRowPerPage: (
      state,
      action: PayloadAction<number>,
    ) => {
      state.nutritionCategoriesListRowPerPage = action.payload;
    },
    setGroupsOptionsList: (
      state,
      action: PayloadAction<{ groups: Group[]; storeId: number }>,
    ) => {
      state.groupsOptionsList = action.payload.groups;
      state.groupsOptionsStoreId = action.payload.storeId;
    },
    setGroupsOptionsLoading: (state, action: PayloadAction<boolean>) => {
      state.isGroupsOptionsLoading = action.payload;
    },
    clearGroupsOptionsList: (state) => {
      state.groupsOptionsList = [];
      state.groupsOptionsStoreId = null;
    },
  },
});

// Action creators are generated for each case reducer function
export const {
  setGroupsList,
  setGroupsLoading,
  setGroupsListRowPerPage,
  setGroupsFilters,
  setGroupSubmitting,
  setLocalizedGroupsList,
  setLocalizedGroupsLoading,
  setLocalizedGroupsListRowPerPage,
  setLocalizedGroupsSubmitting,
  setGroupsThemes,
  setGroupThemesSubmitting,
  setGroupThemesLoading,
  setNutritionCategoriesList,
  setNutritionCategoriesLoading,
  setNutritionCategoriesSubmitting,
  setNutritionCategoriesListRowPerPage,
  setGroupsOptionsList,
  setGroupsOptionsLoading,
  clearGroupsOptionsList,
} = groupsSlice.actions;
export default groupsSlice.reducer;

type GetGroupsListActionParams = {
  continutationToken?: string | null;
  storeId: number;
};

export const getGroupsListAction = ({
  continutationToken = null,
  storeId,
}: GetGroupsListActionParams) => {
  return async (dispatch: AppDispatch, getState: () => RootState) => {
    try {
      const { filters, groupsListRowPerPage } = getState().groups;
      let query = "";
      query =
        "?" +
        createQuery({
          filter: {
            ...filters,
            limit: groupsListRowPerPage,
            continutationToken,
          },
        });
      dispatch(setGroupsLoading(true));
      const { data } = await getGroupListsApi(storeId, query);
      dispatch(setGroupsLoading(false));

      const pagination = data.pagination;
      const groups = data.data;

      dispatch(setGroupsList({ groups, pagination }));
    } catch (error) {
      dispatch(setGroupsLoading(false));
      const message = errorHandler(error);
      dispatch(alertMessage(message, "error"));
    }
  };
};

export const getGroupsAction = ({
  storeId,
  search = "",
  forceRefresh = false,
}: {
  storeId: number;
  search?: string;
  forceRefresh?: boolean;
}) => {
  return async (dispatch: AppDispatch, getState: () => RootState) => {
    const { groupsOptionsList, groupsOptionsStoreId } = getState().groups;
    const hasCachedGroups =
      !search &&
      !forceRefresh &&
      groupsOptionsList.length > 0 &&
      groupsOptionsStoreId === storeId;

    if (hasCachedGroups) {
      return groupsOptionsList;
    }

    try {
      dispatch(setGroupsOptionsLoading(true));
      const query =
        "?" +
        createQuery({
          filter: {
            limit: 1000,
            search,
          },
        });

      const { data } = await getGroupListsApi(storeId, query);
      const groups = data.data;
      dispatch(setGroupsOptionsLoading(false));

      if (!search) {
        dispatch(setGroupsOptionsList({ groups, storeId }));
      }

      return groups;
    } catch (error) {
      dispatch(setGroupsOptionsLoading(false));
      const message = errorHandler(error);
      dispatch(alertMessage(message, "error"));
    }
  };
};

export const postGroupAction = (payload: GroupPayload) => {
  return async (dispatch: AppDispatch) => {
    try {
      const { storeId } = getActiveConfig();
      dispatch(setGroupSubmitting(true));
      const { data } = await postGroupApi({
        payload,
        storeId: storeId,
      });
      dispatch(setGroupSubmitting(false));
      dispatch(alertMessage("Group added successfully", "success"));
      return data;
    } catch (error) {
      dispatch(setGroupSubmitting(false));
      const message = errorHandler(error);
      dispatch(alertMessage(message, "error"));
    }
  };
};

export const putGroupAction = (groupId: number, payload: GroupPayload) => {
  return async (dispatch: AppDispatch) => {
    try {
      const { storeId } = getActiveConfig();
      dispatch(setGroupSubmitting(true));
      const { data } = await putGroupApi({
        payload,
        storeId: storeId,
        groupId,
      });
      dispatch(setGroupSubmitting(false));
      dispatch(alertMessage("Group updated successfully", "success"));
      return data;
    } catch (error) {
      dispatch(setGroupSubmitting(false));
      const message = errorHandler(error);
      dispatch(alertMessage(message, "error"));
    }
  };
};

type GetLocalizedGroupsActionParams = {
  storeId: number;
  groupId: number;
  continutationToken?: string | null;
};

export const getLocalizedGroupsAction = ({
  storeId,
  groupId,
  continutationToken,
}: GetLocalizedGroupsActionParams) => {
  return async (dispatch: AppDispatch, getState: () => RootState) => {
    try {
      const { localizedGroupsListRowPerPage } = getState().groups;
      let query = "";
      query =
        "?" +
        createQuery({
          filter: {
            limit: localizedGroupsListRowPerPage,
            continutationToken,
          },
        });

      dispatch(setLocalizedGroupsLoading(true));
      const { data } = await getLocalizedGroupsApi({ storeId, groupId, query });
      dispatch(setLocalizedGroupsLoading(false));

      const pagination = data.pagination;
      const groups = data.data;

      dispatch(setLocalizedGroupsList({ groups, pagination }));
    } catch (error) {
      dispatch(setLocalizedGroupsLoading(false));
      const message = errorHandler(error);
      dispatch(alertMessage(message, "error"));
    }
  };
};

type PostLocalizedGroupActionParams = {
  storeId: number;
  groupId: number;
  payload: LocalizedGroupPayload;
};
export const postLocalizedGroupAction = ({
  storeId,
  groupId,
  payload,
}: PostLocalizedGroupActionParams) => {
  return async (dispatch: AppDispatch) => {
    try {
      dispatch(setLocalizedGroupsSubmitting(true));
      const { data } = await postLocalizedGroupApi({
        storeId,
        groupId,
        payload,
      });
      dispatch(setLocalizedGroupsSubmitting(false));
      dispatch(alertMessage("Localized group added successfully", "success"));
      return data;
    } catch (error) {
      dispatch(setLocalizedGroupsSubmitting(false));
      const message = errorHandler(error);
      dispatch(alertMessage(message, "error"));
    }
  };
};

export const deleteLocalizedGroupAction = ({
  storeId,
  groupId,
  locale,
}: {
  storeId: number;
  groupId: number;
  locale: string;
}) => {
  return async (dispatch: AppDispatch) => {
    try {
      const { data } = await deleteLocalizedGroupApi({
        storeId,
        groupId,
        locale,
      });
      dispatch(alertMessage("Localized group deleted successfully", "success"));
      return data;
    } catch (error) {
      const message = errorHandler(error);
      dispatch(alertMessage(message, "error"));
    }
  };
};

export const getGroupsThemesAction = () => {
  return async (dispatch: AppDispatch) => {
    try {
      const { storeId } = getActiveConfig();
      dispatch(setGroupThemesLoading(true));
      const { data } = await getGroupsThemeApi(storeId);
      dispatch(setGroupThemesLoading(false));
      const groupsThemes = data.data;
      dispatch(setGroupsThemes(groupsThemes));
    } catch (error) {
      dispatch(setGroupThemesLoading(false));
      const message = errorHandler(error);
      dispatch(alertMessage(message, "error"));
    }
  };
};
export const postGroupsThemeAction = (payload: GroupThemeColors) => {
  return async (dispatch: AppDispatch) => {
    try {
      const { storeId } = getActiveConfig();
      dispatch(setGroupThemesSubmitting(true));
      const { data } = await postGroupsThemeApi(storeId, payload);
      dispatch(getGroupsThemesAction());
      dispatch(setGroupThemesSubmitting(false));
      dispatch(alertMessage("Group theme added successfully", "success"));
      return data;
    } catch (error) {
      dispatch(setGroupThemesSubmitting(false));
      const message = errorHandler(error);
      dispatch(alertMessage(message, "error"));
    }
  };
};
export const putGroupsThemeAction = (
  payload: GroupThemeColors,
  themeId: number,
) => {
  return async (dispatch: AppDispatch) => {
    try {
      const { storeId } = getActiveConfig();
      dispatch(setGroupThemesSubmitting(true));
      const { data } = await putGroupsThemeApi(storeId, payload, themeId);
      dispatch(getGroupsThemesAction());
      dispatch(setGroupThemesSubmitting(false));
      dispatch(alertMessage("Group theme updated successfully", "success"));
      return data;
    } catch (error) {
      dispatch(setGroupThemesSubmitting(false));
      const message = errorHandler(error);
      dispatch(alertMessage(message, "error"));
    }
  };
};
export const deleteGroupsThemeAction = (themeId: number) => {
  return async (dispatch: AppDispatch) => {
    try {
      const { storeId } = getActiveConfig();
      dispatch(setGroupThemesSubmitting(true));
      const { data } = await deleteGroupsThemeApi(storeId, themeId);
      dispatch(getGroupsThemesAction());
      dispatch(setGroupThemesSubmitting(false));
      dispatch(alertMessage("Group theme deleted successfully", "success"));
      return data;
    } catch (error) {
      dispatch(setGroupThemesSubmitting(false));
      const message = errorHandler(error);
      dispatch(alertMessage(message, "error"));
    }
  };
};

// Nutrition Categories API
export const getNutritionCategoriesAction = () => {
  return async (dispatch: AppDispatch) => {
    try {
      const { storeId } = getActiveConfig();
      const { data } = await getNutritionCategoriesApi(storeId);
      const nutritionCategories = data.data;
      dispatch(setNutritionCategoriesList(nutritionCategories));
    } catch (error) {
      const message = errorHandler(error);
      dispatch(alertMessage(message, "error"));
    }
  };
};

export const postNutritionCategoryAction = (payload: Omit<NutritionCategory, "id">) => {
  return async (dispatch: AppDispatch) => {
    try {
      const { storeId } = getActiveConfig();
      dispatch(setNutritionCategoriesSubmitting(true));
      const { data } = await postNutritionCategoryApi(storeId, payload);
      dispatch(setNutritionCategoriesSubmitting(false));
      dispatch(getNutritionCategoriesAction());
      dispatch(alertMessage("Nutrition category added successfully", "success"));
      return data;
    } catch (error) {
      const message = errorHandler(error);
      dispatch(alertMessage(message, "error"));
    }
  };
};

export const putNutritionCategoryAction = (payload: NutritionCategory) => {
  return async (dispatch: AppDispatch) => {
    try {
      const { storeId } = getActiveConfig();
      dispatch(setNutritionCategoriesSubmitting(true));
      const { data } = await putNutritionCategoryApi(storeId, payload);
      dispatch(setNutritionCategoriesSubmitting(false));
      dispatch(getNutritionCategoriesAction());
      dispatch(alertMessage("Nutrition category updated successfully", "success"));
      return data;
    } catch (error) {
      const message = errorHandler(error);
      dispatch(alertMessage(message, "error"));
    }
  };
};
